import apiFetch from "../../../shared/services/apiClient";
import { createWithPersona } from "./createWithPersona";
import { createFilial } from "./createFilial";

export async function createResidentSequence(formData) {
  // Campos catastrales opcionales: enviar null (no "") para no chocar con la
  // restricción de unicidad cuando van vacíos (modo cliente).
  const nullIfEmpty = (v) => {
    const t = (v ?? "").toString().trim();
    return t === "" ? null : t;
  };

  // En modo cliente no hay un código manual: el identificador (idFilial) ES el
  // nombre del cliente, de modo que el nombre aparezca en todo el sistema
  // (catálogo, facturas, estados de cuenta) sin un código artificial tipo CLI-0001.
  const esCliente = (localStorage.getItem("condominio_tipo") || "condominio") === "cliente";
  // Para clientes el identificador es el NOMBRE (no el nombre completo) para un
  // mejor orden visual: razón social si es jurídica, o el nombre de pila.
  const nombreCliente = (
    formData.propietario.nombre_sociedad?.trim() ||
    formData.propietario.nombre?.trim() ||
    `${formData.propietario.nombre || ""} ${formData.propietario.apellidos || ""}`.trim()
  );
  const idFilial = esCliente ? nombreCliente : formData.filial.idFilial;

  // Crear filial (condominio lo inyecta el backend desde request.user)
  const filial = await createFilial({
    idFilial,
    numPlano:        nullIfEmpty(formData.filial.numPlano),
    numFolio:        nullIfEmpty(formData.filial.numFolio),
    tipo_propiedad:  nullIfEmpty(formData.filial.tipoPropiedad),
  });

  const filialId = filial.id;

  // Crear propietario — incluye todos los campos nuevos
  const propietarioRes = await createWithPersona(
    "/api/residents/propietario/",
    {
      cedula:          formData.propietario.cedula,
      nombre:          formData.propietario.nombre          || '',
      apellidos:       formData.propietario.apellidos       || '',
      nombre_sociedad: formData.propietario.nombre_sociedad?.trim() || '',
      tipo_cedula:         formData.propietario.tipo_cedula         || '',
      direccion_provincia: formData.propietario.direccion_provincia || '',
      direccion_canton:    formData.propietario.direccion_canton    || '',
      direccion_distrito:  formData.propietario.direccion_distrito   || '',
      direccion_senas:     formData.propietario.direccion_senas      || '',
    },
    {
      filial:            filialId,
      telefono_casa:     formData.propietario.telefono_casa?.trim()    || null,
      telefono_cel:      formData.propietario.telefono_cel?.trim()     || null,
      telefono_trabajo:  formData.propietario.telefono_trabajo?.trim() || null,
      notas:             formData.propietario.notas?.trim()            || "",
    },
  );

  if (!propietarioRes.ok) {
    const err = await propietarioRes.json().catch(() => ({}));
    throw new Error('Error al crear propietario: ' + JSON.stringify(err));
  }
  const propietario   = await propietarioRes.json();
  const propietarioId = propietario.id;

  // Email principal
  await apiFetch("/api/residents/emailPropietario/", {
    method: "POST",
    body: JSON.stringify({
      propietario: propietarioId,
      email:       formData.propietario.emailPrincipal,
      principal:   true,
    }),
  });

  // Emails extra
  await Promise.all(
    (formData.propietario.emailExtras || [])
      .filter(item => item.email?.trim())
      .map(item =>
        apiFetch("/api/residents/emailPropietario/", {
          method: "POST",
          body: JSON.stringify({
            propietario: propietarioId,
            email:       item.email.trim(),
            principal:   false,
          }),
        })
      )
  );

  // Copropietarios
  await Promise.all(
    formData.copropietarios.map(item =>
      createWithPersona("/api/residents/copropietario/", {
        cedula:          item.cedula,
        nombre:          item.nombre,
        apellidos:       item.apellidos,
        nombre_sociedad: item.nombre_sociedad?.trim() || "",
      }, {
        filial:           filialId,
        telefono_casa:    item.telefono_casa?.trim()    || null,
        telefono_cel:     item.telefono_cel?.trim()     || null,
        telefono_trabajo: item.telefono_trabajo?.trim() || null,
        notas:            item.notas?.trim()            || "",
      })
    )
  );

  // Autorizados
  await Promise.all(
    formData.autorizados.map(item =>
      createWithPersona("/api/residents/autorizado/", {
        cedula:    item.cedula,
        nombre:    item.nombre,
        apellidos: item.apellidos,
      }, {
        filial:        filialId,
        tipo:          item.relacion,
      })
    )
  );

  return propietario;
}


// ═══════════════════════════════════════════════════════════════════════════
// Residente completo (modo condominio): se crea y se edita de una sola vez,
// en una transacción del backend (filial + propietario + correos + vehículos
// + copropietarios + autorizados).
// ═══════════════════════════════════════════════════════════════════════════

let _llave = 0;
// Llave local para las listas del asistente (editar/quitar antes de guardar).
// El id real de la base va aparte, en dbId.
export function nuevaLlave() {
  _llave += 1;
  return `${Date.now()}-${_llave}`;
}

const limpiarVehiculos = (lista) =>
  (lista || [])
    .map((v) => ({ placa: (v.placa || "").trim().toUpperCase(), extranjera: !!v.extranjera }))
    .filter((v) => v.placa);

const vehiculosDeApi = (lista) =>
  (lista || []).map((v) => ({ placa: v.placa, extranjera: !!v.extranjera }));

/** Datos de GET /filial/{id}/completo/ -> forma del formData del asistente. */
export function residenteDesdeApi(data) {
  const p = data.propietario;
  const emails = p?.emails || [];
  const principal = emails.find((e) => e.principal) || emails[0];
  return {
    filial: {
      id:            data.filial.id,
      idFilial:      data.filial.idFilial || "",
      numPlano:      data.filial.numPlano || "",
      numFolio:      data.filial.numFolio || "",
      tipoPropiedad: data.filial.tipo_propiedad || "",
    },
    propietario: {
      id:               p?.id || null,
      cedula:           p?.cedula || "",
      tipo_cedula:      p?.tipo_cedula || "",
      es_sociedad:      !!p?.nombre_sociedad,
      nombre:           p?.nombre || "",
      apellidos:        p?.apellidos || "",
      nombre_sociedad:  p?.nombre_sociedad || "",
      telefono_casa:    p?.telefono_casa || "",
      telefono_cel:     p?.telefono_cel || "",
      telefono_trabajo: p?.telefono_trabajo || "",
      notas:            p?.notas || "",
      sin_correo:       !!p?.sin_correo,
      emailPrincipal:   principal?.email || "",
      emailExtras:      emails
        .filter((e) => e !== principal)
        .map((e) => ({ id: nuevaLlave(), email: e.email })),
      vehiculos:        vehiculosDeApi(p?.vehiculos),
      direccion_provincia: p?.direccion_provincia || "",
      direccion_canton:    p?.direccion_canton || "",
      direccion_distrito:  p?.direccion_distrito || "",
      direccion_senas:     p?.direccion_senas || "",
    },
    copropietarios: (data.copropietarios || []).map((c) => ({
      id:               nuevaLlave(),
      dbId:             c.id,
      cedula:           c.cedula || "",
      es_sociedad:      !!c.nombre_sociedad,
      nombre:           c.nombre || "",
      apellidos:        c.apellidos || "",
      nombre_sociedad:  c.nombre_sociedad || "",
      telefono_casa:    c.telefono_casa || "",
      telefono_cel:     c.telefono_cel || "",
      telefono_trabajo: c.telefono_trabajo || "",
      notas:            c.notas || "",
      vehiculos:        vehiculosDeApi(c.vehiculos),
    })),
    autorizados: (data.autorizados || []).map((a) => ({
      id:        nuevaLlave(),
      dbId:      a.id,
      cedula:    a.cedula || "",
      nombre:    a.nombre || "",
      apellidos: a.apellidos || "",
      relacion:  a.tipo || "Familiar",
      vehiculos: vehiculosDeApi(a.vehiculos),
    })),
  };
}

/** formData del asistente -> cuerpo de crear-completo / completo (PUT). */
export function residenteAPayload(formData) {
  const p = formData.propietario;
  const emails = p.sin_correo
    ? []
    : [
        { email: (p.emailPrincipal || "").trim(), principal: true },
        ...(p.emailExtras || []).map((e) => ({ email: (e.email || "").trim(), principal: false })),
      ].filter((e) => e.email);
  return {
    filial: {
      idFilial:       (formData.filial.idFilial || "").trim(),
      numPlano:       (formData.filial.numPlano || "").trim(),
      numFolio:       (formData.filial.numFolio || "").trim(),
      tipo_propiedad: formData.filial.tipoPropiedad || "",
    },
    propietario: {
      id:               p.id || null,
      cedula:           (p.cedula || "").trim(),
      es_sociedad:      !!p.es_sociedad,
      nombre:           p.nombre || "",
      apellidos:        p.apellidos || "",
      nombre_sociedad:  p.nombre_sociedad || "",
      telefono_casa:    p.telefono_casa || "",
      telefono_cel:     p.telefono_cel || "",
      telefono_trabajo: p.telefono_trabajo || "",
      notas:            p.notas || "",
      sin_correo:       !!p.sin_correo,
      emails,
      vehiculos:        limpiarVehiculos(p.vehiculos),
    },
    copropietarios: (formData.copropietarios || []).map((c) => ({
      id:               c.dbId || null,
      cedula:           (c.cedula || "").trim(),
      es_sociedad:      !!c.es_sociedad,
      nombre:           c.nombre || "",
      apellidos:        c.apellidos || "",
      nombre_sociedad:  c.nombre_sociedad || "",
      telefono_casa:    c.telefono_casa || "",
      telefono_cel:     c.telefono_cel || "",
      telefono_trabajo: c.telefono_trabajo || "",
      notas:            c.notas || "",
      vehiculos:        limpiarVehiculos(c.vehiculos),
    })),
    autorizados: (formData.autorizados || []).map((a) => ({
      id:        a.dbId || null,
      cedula:    (a.cedula || "").trim(),
      nombre:    a.nombre || "",
      apellidos: a.apellidos || "",
      tipo:      a.relacion || "Familiar",
      vehiculos: limpiarVehiculos(a.vehiculos),
    })),
  };
}

async function enviarResidente(url, method, formData) {
  const res = await apiFetch(url, { method, body: JSON.stringify(residenteAPayload(formData)) });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data.detail || data.error || "No se pudo guardar. Revisá los datos e intentá de nuevo.");
    err.errores = data.errores || {}; // { "filial.numFolio": "...", "propietario.vehiculos.0": "..." }
    throw err;
  }
  return data;
}

export function crearResidenteCompleto(formData) {
  return enviarResidente("/api/residents/filial/crear-completo/", "POST", formData);
}

export function actualizarResidenteCompleto(filialId, formData) {
  return enviarResidente(`/api/residents/filial/${filialId}/completo/`, "PUT", formData);
}

export async function obtenerResidenteCompleto(filialId) {
  const res = await apiFetch(`/api/residents/filial/${filialId}/completo/`);
  if (!res.ok) throw new Error("No se encontró la filial.");
  return residenteDesdeApi(await res.json());
}
